import { Ceil, Floor, ToNumber } from "@pdfking/math"
import { Constant, UInt8ToString } from "@pdfking/tfunc"

import { DefaultMinimap as M, type WasmImports } from "#minimap"

import type { FSInstance } from "../public/types/WasmInstance"

// WASI/Emscripten errno values
const EBADF = 8
const EINVAL = 28

// fcntl commands
const F_DUPFD = 0
const F_GETFD = 1
const F_SETFD = 2
const F_GETFL = 3
const F_SETFL = 4

export type { FSInstance }

export type BaseFD = { flags: number }

export type FSUtils = {
  mem: () => ArrayBuffer
  heapU8: () => Uint8Array
  heapU32: () => Uint32Array
  heap32: () => Int32Array
  dv: () => DataView
  getStr: (ptr: number) => string
  readIov: (d: DataView, off: number) => { ptr: number; len: number }
  writeSize: (d: DataView, off: number, v: number) => void
  iovStride: number
  writeStat: (buf: number, size: number, isDir: boolean, mtimeMs?: number) => number
  getVarargI: () => number
  setVarargs: (v: number) => void
}

export const CreateFSContext = <T extends number | bigint, FD extends BaseFD>(
  ctx: FSInstance<T>,
  fds: Map<number, FD>
) => {
  const { memory64 } = ctx

  const mem = () => ctx.exports[M.memory].buffer
  const heapU8 = () => new Uint8Array(mem())
  const heapU32 = () => new Uint32Array(mem())
  const heap32 = () => new Int32Array(mem())
  const dv = () => new DataView(mem())

  const iovStride = memory64 ? 16 : 8
  const readIov = memory64
    ? (d: DataView, off: number) => ({
        ptr: ToNumber(d.getBigUint64(off, true)),
        len: ToNumber(d.getBigUint64(off + 8, true)),
      })
    : (d: DataView, off: number) => ({
        ptr: d.getUint32(off, true),
        len: d.getUint32(off + 4, true),
      })
  const writeSize = memory64
    ? (d: DataView, off: number, v: number) => d.setBigUint64(off, BigInt(v), true)
    : (d: DataView, off: number, v: number) => d.setUint32(off, v, true)

  const getStr = (ptr: number): string => {
    const bytes = heapU8()
    let end = ptr
    while (bytes[end] !== 0) end++
    return UInt8ToString(bytes.subarray(ptr, end))
  }

  let varargs = 0
  const getVarargI = (): number => {
    const ret = heap32()[varargs >> 2]!
    varargs += 4
    return ret
  }
  const setVarargs = (v: number) => {
    varargs = v
  }

  const writeStat = (buf: number, size: number, isDir: boolean, mtimeMs?: number): number => {
    const d = dv()
    const now = mtimeMs ?? Date.now()
    const timeSec = BigInt(Floor(now / 1000))
    const timeNsec = BigInt((now % 1000) * 1000000)
    const bSize = BigInt(size)
    const mode = isDir ? 0o40755 : 0o100644

    if (memory64) {
      d.setUint32(buf, 1, true) //  0 st_dev
      d.setUint32(buf + 4, mode, true) //  4 st_mode
      d.setBigUint64(buf + 8, 1n, true) //  8 st_nlink (u64, pointer-sized)
      d.setUint32(buf + 16, 0, true) // 16 st_uid
      d.setUint32(buf + 20, 0, true) // 20 st_gid
      d.setUint32(buf + 24, 0, true) // 24 st_rdev
      // 28: 4 bytes padding for off_t alignment
      d.setBigInt64(buf + 32, bSize, true) // 32 st_size
      d.setInt32(buf + 40, 4096, true) // 40 st_blksize (always int)
      d.setInt32(buf + 44, Ceil(size / 512), true) // 44 st_blocks (always int)
      d.setBigInt64(buf + 48, timeSec, true) // 48 atim.sec
      d.setBigInt64(buf + 56, timeNsec, true) // 56 atim.nsec (long=8)
      d.setBigInt64(buf + 64, timeSec, true) // 64 mtim.sec
      d.setBigInt64(buf + 72, timeNsec, true) // 72 mtim.nsec (long=8)
      d.setBigInt64(buf + 80, timeSec, true) // 80 ctim.sec
      d.setBigInt64(buf + 88, timeNsec, true) // 88 ctim.nsec (long=8)
      d.setBigUint64(buf + 96, 1n, true) // 96 st_ino
    } else {
      const h = heapU32()
      h[buf >> 2] = 1 //  0 st_dev
      h[(buf + 4) >> 2] = mode //  4 st_mode
      h[(buf + 8) >> 2] = 1 //  8 st_nlink
      h[(buf + 12) >> 2] = 0 // 12 st_uid
      h[(buf + 16) >> 2] = 0 // 16 st_gid
      h[(buf + 20) >> 2] = 0 // 20 st_rdev
      d.setBigInt64(buf + 24, bSize, true) // 24 st_size
      d.setInt32(buf + 32, 4096, true) // 32 st_blksize
      d.setInt32(buf + 36, Ceil(size / 512), true) // 36 st_blocks
      d.setBigInt64(buf + 40, timeSec, true) // 40 atim.sec
      d.setInt32(buf + 48, ToNumber(timeNsec), true) // 48 atim.nsec
      d.setBigInt64(buf + 56, timeSec, true) // 56 mtim.sec
      d.setInt32(buf + 64, ToNumber(timeNsec), true) // 64 mtim.nsec
      d.setBigInt64(buf + 72, timeSec, true) // 72 ctim.sec
      d.setInt32(buf + 80, ToNumber(timeNsec), true) // 80 ctim.nsec
      d.setBigUint64(buf + 88, 1n, true) // 88 st_ino
    }
    return 0
  }

  const utils: FSUtils = {
    mem,
    heapU8,
    heapU32,
    heap32,
    dv,
    getStr,
    readIov,
    writeSize,
    iovStride,
    writeStat,
    getVarargI,
    setVarargs,
  }

  // Common signatures identical in browser and node
  const common = {
    [M.__syscall_ioctl]: Constant(0),

    [M.__syscall_fcntl64]: (fd: number, cmd: number, va: number | bigint): number => {
      setVarargs(ToNumber(va))
      const openFd = fds.get(fd)
      if (!openFd && fd > 2) return -EBADF

      switch (cmd) {
        case F_DUPFD: {
          let arg = getVarargI()
          if (arg < 0) return -EINVAL
          while (fds.has(arg)) arg++
          if (openFd) fds.set(arg, { ...openFd })
          return arg
        }
        case F_GETFD:
        case F_SETFD:
          return 0
        case F_GETFL:
          return openFd?.flags ?? 0
        case F_SETFL: {
          const arg = getVarargI()
          if (openFd) openFd.flags |= arg
          return 0
        }
        default:
          return -EINVAL
      }
    },
  } satisfies Partial<WasmImports<T>>

  return { utils, common }
}
